/**
 * propertyTranslations.ts
 *
 * Definisce le traduzioni per le diverse tipologie di immobili supportate.
 * Utilizza un ID numerico e fornisce le traduzioni
 * corrispondenti per le lingue supportate dall'applicazione.
 */ 

// Definisce i codici lingua supportati (standard ISO 639-1)
export type LanguageCode = 'it' | 'en' | 'de' | 'sq' | 'ru' | 'fr' | 'es' | 'pt' ;

// Definisce la struttura per le traduzioni di una singola tipologia
// Mappa ogni codice lingua supportato alla stringa tradotta corrispondente.
export type Translations = {
  [lang in LanguageCode]: string;
};

// Definisce la struttura per l'oggetto completo di una tipologia tradotta
export type TranslationDict = {
  id: number;                 // ID univoco della tipologia
  translations: Translations; // Oggetto contenente le traduzioni per lingua
};

export const contractTranslations: TranslationDict[] = [
    { id: 1, translations: { it: "Vendita", en: "Sale", de: "Verkauf", sq: "Shitje", ru: "Продажа", fr: "Vente", es: "Venta", pt: "Venda" } },
    { id: 2, translations: { it: "Affitto", en: "Rent", de: "Miete", sq: "Qira", ru: "Аренда", fr: "Location", es: "Alquiler", pt: "Aluguel" } },
    { id: 4, translations: { it: "Affitto Commerciale", en: "Commercial Rent", de: "Gewerbe Miete", sq: "Qira komerciale", ru: "Коммерческая аренда", fr: "Location commerciale", es: "Alquiler comercial", pt: "Aluguel comercial" } },
    { id: 5, translations: { it: "Affitto Vacanze", en: "Vacation Rent", de: "Urlaub Miete", sq: "Qira pushimi", ru: "Аренда на отдых", fr: "Location de vacances", es: "Alquiler de vacaciones", pt: "Aluguel de férias" } }
];

export const propertyStatusTranslations: TranslationDict[] = [
    { id: 0, translations: { it: "N/D", en: "N/A", de: "k.A.", sq: "P/N", ru: "Н/Д", fr: "N/D", es: "N/D", pt: "N/D" } },
    { id: 1, translations: { it: "Buono", en: "Good", de: "Gut", sq: "E mirë", ru: "Хорошее", fr: "Bon", es: "Bueno", pt: "Bom" } },
    { id: 2, translations: { it: "Da ristrutturare", en: "To be renovated", de: "Renovierungsbedürftig", sq: "Për t'u rinovuar", ru: "Требует ремонта", fr: "À rénover", es: "Para reformar", pt: "A renovar" } },
    { id: 3, translations: { it: "Discreto", en: "Fair", de: "Ausreichend", sq: "I pranueshëm", ru: "Удовлетворительное", fr: "Passable", es: "Aceptable", pt: "Razoável" } },
    { id: 4, translations: { it: "Nuovo/in costruzione", en: "New/under construction", de: "Neu/im Bau", sq: "I ri/në ndërtim", ru: "Новый/в строительстве", fr: "Neuf/en construction", es: "Nuevo/en construcción", pt: "Novo/em construção" } },
    { id: 5, translations: { it: "Ottimo", en: "Excellent", de: "Ausgezeichnet", sq: "Shkëlqyeshëm", ru: "Отличное", fr: "Excellent", es: "Excelente", pt: "Excelente" } },
    { id: 6, translations: { it: "Ristrutturato", en: "Renovated", de: "Renoviert", sq: "I rinovuar", ru: "Отремонтировано", fr: "Rénové", es: "Reformado", pt: "Renovado" } },
    { id: 7, translations: { it: "Da rivedere", en: "Needs review", de: "Überholungsbedürftig", sq: "Ka nevojë për shqyrtim", ru: "Требует обновления", fr: "À revoir", es: "A revisar", pt: "A rever" } }
];

export const heatingTypeTranslations: TranslationDict[] = [
    { id: 1, translations: { it: "Si", en: "Yes", de: "Ja", sq: "Po", ru: "Да", fr: "Oui", es: "Sí", pt: "Sim" } },
    { id: 2, translations: { it: "No", en: "No", de: "Nein", sq: "Jo", ru: "Нет", fr: "Non", es: "No", pt: "Não" } },
    { id: 3, translations: { it: "Autonomo", en: "Independent", de: "Unabhängig", sq: "I pavarur", ru: "Автономное", fr: "Indépendant", es: "Independiente", pt: "Independente" } },
    { id: 4, translations: { it: "Condominiale", en: "Central (shared)", de: "Zentralheizung", sq: "Qendror (ndaj)", ru: "Центральное (общее)", fr: "Centralisé (partagé)", es: "Centralizado (común)", pt: "Central (compartilhado)" } },
    { id: 5, translations: { it: "A Pavimento", en: "Underfloor", de: "Fußbodenheizung", sq: "Ngrohje në dysheme", ru: "Тёплый пол", fr: "Plancher chauffant", es: "Suelo radiante", pt: "Aquecimento de piso" } },
    { id: 6, translations: { it: "A Radiatore", en: "Radiator", de: "Heizkörper", sq: "Me radiator", ru: "Радиаторное", fr: "Radiateur", es: "Radiador", pt: "Radiador" } },
    { id: 7, translations: { it: "A Stufa", en: "Stove", de: "Ofen", sq: "Me sobë", ru: "Печное", fr: "Poêle", es: "Estufa", pt: "Fogão / Estufa" } },
    { id: 8, translations: { it: "Ad aria", en: "Air-based", de: "Luftheizung", sq: "Me ajër", ru: "Воздушное", fr: "Par air", es: "Por aire", pt: "Por ar" } }
];

export const heatingFuelNameTranslations: TranslationDict[] = [
  { id: 1, translations: { it: "Gas naturale", en: "Natural gas", de: "Erdgas", sq: "Gaz natyror", ru: "Природный газ", fr: "Gaz naturel", es: "Gas natural", pt: "Gás natural" } },
  { id: 2, translations: { it: "Gas propano/butano", en: "Propane/butane gas", de: "Propan-/Butangas", sq: "Gaz propan/butan", ru: "Пропан/бутан", fr: "Gaz propane/butane", es: "Gas propano/butano", pt: "Gás propano/butano" } },
  { id: 3, translations: { it: "Elettrica", en: "Electric", de: "Elektrisch", sq: "Elektrike", ru: "Электрическое", fr: "Électrique", es: "Eléctrica", pt: "Elétrica" } },
  { id: 4, translations: { it: "Condizionatori caldo/freddo", en: "Hot/cold air conditioners", de: "Kalt-/Warmklimaanlagen", sq: "Kondicionerë ngrohje/ftohje", ru: "Кондиционеры тепло/холод", fr: "Climatiseurs chaud/froid", es: "Aires acondicionados frío/calor", pt: "Ar-condicionados quente/frio" } },
  { id: 5, translations: { it: "Gasolio", en: "Diesel", de: "Diesel", sq: "Naftë", ru: "Дизельное топливо", fr: "Fioul", es: "Gasoil", pt: "Gasóleo" } }
];

export const furnitureTranslations: TranslationDict[] = [
    { id: 0, translations: { it: "Nessuno", en: "None", de: "Keine", sq: "Asnjë", ru: "Нет", fr: "Aucun", es: "Ninguno", pt: "Nenhum" } },
    { id: 1, translations: { it: "Arredato", en: "Furnished", de: "Möbliert", sq: "E mobiluar", ru: "Меблировано", fr: "Meublé", es: "Amueblado", pt: "Mobilado" } },
    { id: 2, translations: { it: "Non arredato", en: "Unfurnished", de: "Unmöbliert", sq: "Pa mobiluar", ru: "Без мебели", fr: "Non meublé", es: "Sin amueblar", pt: "Sem mobília" } },
    { id: 3, translations: { it: "Parzialmente arredato", en: "Partially furnished", de: "Teilweise möbliert", sq: "Pjesërisht i mobiluar", ru: "Частично меблировано", fr: "Partiellement meublé", es: "Parcialmente amueblado", pt: "Parcialmente mobilado" } }
];

export const floorTranslations: TranslationDict[] = [
    { id: 0, translations: { it: "N.D.", en: "N/A", de: "N/V", sq: "N/A", ru: "Н/Д", fr: "N/D", es: "N/D", pt: "N/D" } },
    { id: 1, translations: { it: "Interrato", en: "Basement", de: "Keller", sq: "Kellere", ru: "Подвал", fr: "Sous-sol", es: "Sótano", pt: "Cave" } },
    { id: 2, translations: { it: "Semi interrato", en: "Semi-basement", de: "Halbkeller", sq: "Gjysmë bodrum", ru: "Полуподвал", fr: "Sous-sol semi-enterré", es: "Semi-sótano", pt: "Semi-cave" } },
    { id: 3, translations: { it: "Piano terra", en: "Ground floor", de: "Erdgeschoss", sq: "Kati i parë", ru: "Первый этаж", fr: "Rez-de-chaussée", es: "Planta baja", pt: "R/chão" } },
    { id: 4, translations: { it: "Primo piano", en: "First floor", de: "Erster Stock", sq: "Kati i dytë", ru: "Второй этаж", fr: "Premier étage", es: "Primer piso", pt: "Primeiro andar" } },
    { id: 5, translations: { it: "Secondo piano", en: "Second floor", de: "Zweiter Stock", sq: "Kati i tretë", ru: "Третий этаж", fr: "Deuxième étage", es: "Segundo piso", pt: "Segundo andar" } },
    { id: 6, translations: { it: "Terzo piano", en: "Third floor", de: "Dritter Stock", sq: "Kati i katërt", ru: "Четвертый этаж", fr: "Troisième étage", es: "Tercer piso", pt: "Terceiro andar" } },
    { id: 7, translations: { it: "Quarto piano", en: "Fourth floor", de: "Vierter Stock", sq: "Kati i pestë", ru: "Пятый этаж", fr: "Quatrième étage", es: "Cuarto piso", pt: "Quarto andar" } },
    { id: 8, translations: { it: "Quinto piano", en: "Fifth floor", de: "Fünfter Stock", sq: "Kati i gjashtë", ru: "Шестой этаж", fr: "Cinquième étage", es: "Quinto piso", pt: "Quinto andar" } },
    { id: 10, translations: { it: "Sesto piano", en: "Sixth floor", de: "Sechster Stock", sq: "Kati i shtatë", ru: "Седьмой этаж", fr: "Sixième étage", es: "Sexto piso", pt: "Sexto andar" } },
    { id: 11, translations: { it: "Settimo piano", en: "Seventh floor", de: "Siebter Stock", sq: "Kati i tetë", ru: "Восьмой этаж", fr: "Septième étage", es: "Séptimo piso", pt: "Sétimo andar" } },
    { id: 12, translations: { it: "Ottavo piano", en: "Eighth floor", de: "Achter Stock", sq: "Kati i nëntë", ru: "Девятый этаж", fr: "Huitième étage", es: "Octavo piso", pt: "Oitavo andar" } },
    { id: 13, translations: { it: "Nono piano", en: "Ninth floor", de: "Neunter Stock", sq: "Kati i dhjetë", ru: "Десятый этаж", fr: "Neuvième étage", es: "Noveno piso", pt: "Nono andar" } },
    { id: 14, translations: { it: "Decimo piano", en: "Tenth floor", de: "Zehnter Stock", sq: "Kati i njëmbëdhjetë", ru: "Десятый этаж", fr: "Dixième étage", es: "Décimo piso", pt: "Décimo andar" } },
    { id: 17, translations: { it: "> Decimo piano", en: "> Tenth floor", de: "> Zehnter Stock", sq: "> Kati i dhjetë", ru: "> Десятый этаж", fr: "> Dixième étage", es: "> Décimo piso", pt: "> Décimo andar" } },
    { id: 19, translations: { it: "Piano rialzato", en: "Mezzanine", de: "Mezzanin", sq: "Kati i ngritur", ru: "Цокольный этаж", fr: "Mezzanine", es: "Entresuelo", pt: "Mezanino" } },
    { id: 20, translations: { it: "Più livelli", en: "Multiple levels", de: "Mehrere Ebenen", sq: "Nivel më shumë", ru: "Многоуровневый", fr: "Plusieurs niveaux", es: "Múltiples niveles", pt: "Múltiplos níveis" } }
]

/**
 * Funzione helper per ottenere la traduzione di un contratto specifica.
 *
 * @param id L'ID numerico della tipologia.
 * @param lang Il codice della lingua desiderata (es. 'it', 'en').
 * @param fallbackLang Il codice della lingua di fallback (default: 'it') da usare se la traduzione richiesta non è disponibile.
 * @returns La stringa tradotta o una stringa di fallback se l'ID non è trovato o nessuna traduzione è disponibile.
**/

export const getContractTranslation = (
    id: number | null | undefined, // Permetti null o undefined come input
    lang: LanguageCode,
    fallbackLang: LanguageCode = 'it'
): string => {
    // Gestisci input nulli o indefiniti
    if (id === null || id === undefined) {
        console.warn(`Tentativo di tradurre Contratto con ID nullo o indefinito.`);
        return `Contratto non specificato`; // O una stringa vuota ''
    }

    const contratto = contractTranslations.find(t => t.id === id);
    if (contratto) {
        // Prova la lingua richiesta, poi il fallback, poi una stringa generica
        return contratto.translations[lang]
               || contratto.translations[fallbackLang]
               || `Contratto ID ${id}`; // Fallback finale se mancano entrambe le lingue
    }
    // Se l'ID non esiste proprio nell'array
    console.warn(`Traduzione non trovata per Contratto ID: ${id}`);
    return `Contratto ID ${id}`; // Fallback per ID non trovato
};

/**
 * Funzione helper per ottenere la traduzione di un contratto specifico.
 *
 * @param id L'ID numerico dello status.
 * @param lang Il codice della lingua desiderata (es. 'it', 'en').
 * @param fallbackLang Il codice della lingua di fallback (default: 'it') da usare se la traduzione richiesta non è disponibile.
 * @returns La stringa tradotta o una stringa di fallback se l'ID non è trovato o nessuna traduzione è disponibile.
**/

export const getPropertyStatusTranslation = (
    id: number | null | undefined, // Permetti null o undefined come input
    lang: LanguageCode,
    fallbackLang: LanguageCode = 'it'
): string => {
    // Gestisci input nulli o indefiniti
    if (id === null || id === undefined) {
        console.warn(`Tentativo di tradurre Status con ID nullo o indefinito.`);
        return `Status non specificato`; // O una stringa vuota ''
    }

    const status = propertyStatusTranslations.find(t => t.id === id);
    if (status) {
        // Prova la lingua richiesta, poi il fallback, poi una stringa generica
        return status.translations[lang]
               || status.translations[fallbackLang]
               || `Contratto ID ${id}`; // Fallback finale se mancano entrambe le lingue
    }
    // Se l'ID non esiste proprio nell'array
    console.warn(`Traduzione non trovata per Status ID: ${id}`);
    return `Status ID ${id}`; // Fallback per ID non trovato
};

/**
 * Funzione helper per ottenere la traduzione di un contratto specifico.
 *
 * @param id L'ID numerico dello Heating Type.
 * @param lang Il codice della lingua desiderata (es. 'it', 'en').
 * @param fallbackLang Il codice della lingua di fallback (default: 'it') da usare se la traduzione richiesta non è disponibile.
 * @returns La stringa tradotta o una stringa di fallback se l'ID non è trovato o nessuna traduzione è disponibile.
**/

export const getHeatingTypeTranslation = (
    id: number | null | undefined, // Permetti null o undefined come input
    lang: LanguageCode,
    fallbackLang: LanguageCode = 'it'
): string => {
    // Gestisci input nulli o indefiniti
    if (id === null || id === undefined) {
        console.warn(`Tentativo di tradurre Heating Type con ID nullo o indefinito.`);
        return `Heating Type non specificato`; // O una stringa vuota ''
    }

    const heatingType = heatingTypeTranslations.find(t => t.id === id);
    if (heatingType) {
        // Prova la lingua richiesta, poi il fallback, poi una stringa generica
        return heatingType.translations[lang]
               || heatingType.translations[fallbackLang]
               || `Heating Type ID ${id}`; // Fallback finale se mancano entrambe le lingue
    }
    // Se l'ID non esiste proprio nell'array
    console.warn(`Traduzione non trovata per Heating Type Typetus ID: ${id}`);
    return `Heating Type ID ${id}`; // Fallback per ID non trovato
};

/**
 * Funzione helper per ottenere la traduzione di un contratto specifico.
 *
 * @param id L'ID numerico dello Fuel Name.
 * @param lang Il codice della lingua desiderata (es. 'it', 'en').
 * @param fallbackLang Il codice della lingua di fallback (default: 'it') da usare se la traduzione richiesta non è disponibile.
 * @returns La stringa tradotta o una stringa di fallback se l'ID non è trovato o nessuna traduzione è disponibile.
**/

export const getHeatingFuelNameTranslation = (
    id: number | null | undefined, // Permetti null o undefined come input
    lang: LanguageCode,
    fallbackLang: LanguageCode = 'it'
): string => {
    // Gestisci input nulli o indefiniti
    if (id === null || id === undefined) {
        console.warn(`Tentativo di tradurre Fuel Name con ID nullo o indefinito.`);
        return `Fuel Name non specificato`; // O una stringa vuota ''
    }

    const fuelName = heatingFuelNameTranslations.find(t => t.id === id);
    if (fuelName) {
        // Prova la lingua richiesta, poi il fallback, poi una stringa generica
        return fuelName.translations[lang]
               || fuelName.translations[fallbackLang]
               || `Fuel Name ID ${id}`; // Fallback finale se mancano entrambe le lingue
    }
    // Se l'ID non esiste proprio nell'array
    console.warn(`Traduzione non trovata per Fuel Name ID: ${id}`);
    return `Fuel Name ID ${id}`; // Fallback per ID non trovato
};

/**
 * Funzione helper per ottenere la traduzione di un contratto specifico.
 *
 * @param id L'ID numerico della Furniture.
 * @param lang Il codice della lingua desiderata (es. 'it', 'en').
 * @param fallbackLang Il codice della lingua di fallback (default: 'it') da usare se la traduzione richiesta non è disponibile.
 * @returns La stringa tradotta o una stringa di fallback se l'ID non è trovato o nessuna traduzione è disponibile.
**/

export const getFurnitureTranslation = (
    id: number | null | undefined, // Permetti null o undefined come input
    lang: LanguageCode,
    fallbackLang: LanguageCode = 'it'
): string => {
    // Gestisci input nulli o indefiniti
    if (id === null || id === undefined) {
        console.warn(`Tentativo di tradurre Furniture con ID nullo o indefinito.`);
        return `Furniture non specificato`; // O una stringa vuota ''
    }

    const furniture = furnitureTranslations.find(t => t.id === id);
    if (furniture) {
        // Prova la lingua richiesta, poi il fallback, poi una stringa generica
        return furniture.translations[lang]
               || furniture.translations[fallbackLang]
               || `Furniture ID ${id}`; // Fallback finale se mancano entrambe le lingue
    }
    // Se l'ID non esiste proprio nell'array
    console.warn(`Traduzione non trovata per Furniture ID: ${id}`);
    return `Furniture ID ${id}`; // Fallback per ID non trovato
};

/**
 * Funzione helper per ottenere la traduzione di un contratto specifico.
 *
 * @param id L'ID numerico della Floor.
 * @param lang Il codice della lingua desiderata (es. 'it', 'en').
 * @param fallbackLang Il codice della lingua di fallback (default: 'it') da usare se la traduzione richiesta non è disponibile.
 * @returns La stringa tradotta o una stringa di fallback se l'ID non è trovato o nessuna traduzione è disponibile.
**/


export const getFloorTranslation = (
    id: number | null | undefined, // Permetti null o undefined come input
    lang: LanguageCode,
    fallbackLang: LanguageCode = 'it'
): string => {
    // Gestisci input nulli o indefiniti
    if (id === null || id === undefined) {
        console.warn(`Tentativo di tradurre Floor con ID nullo o indefinito.`);
        return `Floor non specificato`; // O una stringa vuota ''
    }

    const floor = floorTranslations.find(t => t.id === id);
    if (floor) {
        // Prova la lingua richiesta, poi il fallback, poi una stringa generica
        return floor.translations[lang]
               || floor.translations[fallbackLang]
               || `Floor ID ${id}`; // Fallback finale se mancano entrambe le lingue
    }
    // Se l'ID non esiste proprio nell'array
    console.warn(`Traduzione non trovata per Floor ID: ${id}`);
    return `Floor ID ${id}`; // Fallback per ID non trovato
};

// Esempio di utilizzo:
// import { getContractTranslation, LanguageCode } from './config/elementsMappingProperty';
// const translatedName = getContractTranslation(1, 'fr'); // Ottiene "Vente"
// const translatedNameNotFound = getContractTranslation(999, 'en'); // Ottiene "Contratto ID 999" e logga un warning
// const translatedNameNull = getContractTranslation(null, 'de'); // Ottiene "Contratto non specificato" e logga un warning